"use client";

import { useState, useEffect, useCallback } from "react";
import { Drawer, Spin, message } from "antd";
import Image from "next/image";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import { usePathname, useRouter } from "next/navigation";
import { IoCloseOutline } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import { BsHandbag } from "react-icons/bs";

import "./FashionCartDrawer.scss";
import defaultImage from "../../../../assets/images/defaultImages/alfa-loader.gif";

import {
  displayCart,
  updateCartCount,
  clearCart,
  updateCart,
} from "@/redux/Slices/cartSlice";
import { clearCheckout } from "@/redux/Slices/checkoutSlice";
import {
  clearCartDeliveryCountryCookie,
  syncCartDeliveryCountryCookie,
} from "@/functions/HelperFunctions/index";

export default function FashionCartDrawer({ customIcon }) {
  const dispatch = useDispatch();
  const pathname = usePathname();
  const router = useRouter();

  const fashionCartId = Cookies.get("fashion_cart_id");
  const cartId = Cookies.get("cartId");
  const defaultCurrencyIdFromCookies = Cookies.get("fashionCurrencyId");
  const defaultCurrency = Cookies.get("fashionCurrencyName") || "PKR";
  const showCartDropdown = useSelector((state) => state?.cartSlice?.showCart);

  const [isOpen, setIsOpen] = useState(false);
  const [cartData, setCartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updatingItemId, setUpdatingItemId] = useState(null);

  const useCartId = fashionCartId;
  const isFashionPath = pathname?.includes("fashion");

  const handleClose = () => {
    setIsOpen(false);
    dispatch(displayCart(false));
  };

  const handleGoToCart = () => {
    handleClose();
    if (isFashionPath && useCartId) {
      router.push(`/fashion/cart/checkout?id=${useCartId}`);
    } else if (useCartId) {
      router.push(`/cart/checkout?id=${useCartId}`);
    }
  };

  const clearCartDataFn = () => {
    dispatch(clearCheckout());
    dispatch(clearCart());
    dispatch(updateCart());
    dispatch(updateCartCount(0));
    Cookies.remove("cartId");
    Cookies.remove("fashion_cart_id");
    clearCartDeliveryCountryCookie();
  };

  const fetchCartProducts = useCallback(async () => {
    if (!useCartId) {
      dispatch(updateCartCount(0));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const storeId = defaultCurrencyIdFromCookies || "23";
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND}/order/view_cart?storefront=${storeId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json", cartid: useCartId },
        }
      );
      const data = await res.json();

      if (!res.ok) {
        if (data.detail === "cart not found") clearCartDataFn();
        throw new Error("ViewCart API Failed!");
      }
      setCartData(data);
      dispatch(updateCartCount(data?.line_items?.length || 0));
    } catch (error) {
      console.error("FashionCartDrawer fetch error:", error.message);
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  }, [useCartId, defaultCurrencyIdFromCookies]);

  const updateQuantity = async (lineItem, newQuantity) => {
    if (newQuantity < 1) return;

    const userAgent = Cookies.get("user_agent") || "";

    try {
      setUpdatingItemId(lineItem.id);

      const selectedAddons = lineItem.add_on_templates?.flatMap((template) =>
        template.add_ons?.flatMap((addon) =>
          addon.options
            ?.filter((option) =>
              lineItem.selected_add_ons?.some(
                (sel) => sel.option === option.id
              )
            )
            .map((option) => ({ option: option.id }))
        )
      ) || [];

      const body = {
        line_item: {
          id: lineItem.id,
          quantity: newQuantity,
          variant_price_inventory: lineItem.variant_price_inventory,
          add_ons: selectedAddons,
        },
        user_agent: userAgent,
      };

      const res = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND}/order/update_item_in_cart`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            cartid: useCartId,
          },
          body: JSON.stringify(body),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        message.error(data?.detail || "Failed to update quantity");
        return;
      }

      setCartData(data);
      dispatch(updateCartCount(data?.line_items?.length || 0));
    } catch (error) {
      console.error("Quantity update error:", error.message);
      message.error("Failed to update quantity");
    } finally {
      setUpdatingItemId(null);
    }
  };

  const removeItem = async (lineItem) => {
    const userAgent = Cookies.get("user_agent") || "";

    try {
      setUpdatingItemId(lineItem.id);

      const body = {
        line_id: lineItem.id,
        user_agent: userAgent,
      };

      const res = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND}/order/remove_item_from_cart`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            cartid: useCartId,
          },
          body: JSON.stringify(body),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        message.error(data?.detail || "Failed to remove item");
        return;
      }

      setCartData(data);
      dispatch(updateCartCount(data?.line_items?.length || 0));
      message.success("Item removed from cart");
      syncCartDeliveryCountryCookie(data?.line_items);

      if (!data?.line_items?.length) {
        clearCartDataFn();
      }
    } catch (error) {
      console.error("Remove item error:", error.message);
      message.error("Failed to remove item");
    } finally {
      setUpdatingItemId(null);
    }
  };

  useEffect(() => {
    if ((cartId || fashionCartId) && isOpen) fetchCartProducts();
  }, [cartId, fashionCartId, isOpen, fetchCartProducts]);

  useEffect(() => {
    if (showCartDropdown) setIsOpen(true);
  }, [showCartDropdown]);

  const lineItems = cartData?.line_items || [];
  const itemCount = lineItems.length;

  const subtotal = lineItems.reduce((sum, item) => {
    const price = parseFloat(String(item?.price || "0").replace(/,/g, ""));
    return sum + price * (item?.quantity || 1);
  }, 0);

  const formatPrice = (value) => {
    return value.toLocaleString("en-PK");
  };

  const getSizeLabel = (cartItem) => {
    const selectedAddons = cartItem.add_on_templates?.flatMap((template) =>
      template.add_ons?.flatMap((addon) =>
        addon.options
          ?.filter((option) =>
            cartItem.selected_add_ons?.some(
              (sel) => sel.option === option.id
            )
          )
          .map((option) => ({ ...addon, selected_option: option }))
      )
    ) || [];

    const sizeAddon = selectedAddons.find(
      (a) => a?.name?.toLowerCase() === "size"
    );
    if (sizeAddon) return sizeAddon.selected_option?.name;

    const selectedVariant = cartItem.variants?.find(
      (v) =>
        v?.variant_detail?.variant_id === cartItem.variant_price_inventory
    );

    if (selectedVariant?.option1 && !selectedVariant.option1.includes("Default")) {
      return selectedVariant.option1;
    }

    return null;
  };

  return (
    <div className="fashion-cart-drawer-wrapper">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="fashion-cart-drawer-trigger"
        role="button"
        tabIndex={0}
        aria-label="Open cart"
      >
        {customIcon || <BsHandbag style={{ fontSize: "18px" }} />}
      </div>

      <Drawer
        open={isOpen}
        onClose={handleClose}
        placement="right"
        width={300}
        closable={false}
        className="fashion-cart-drawer"
        styles={{
          header: { display: "none" },
          body: { padding: 0 },
        }}
      >
        <div className="fashion-cart-drawer__header">
          <span className="fashion-cart-drawer__item-count">
            There {itemCount === 1 ? "is" : "are"} {itemCount} item(s) in your
            cart
          </span>
          <button
            className="fashion-cart-drawer__close-btn"
            onClick={handleClose}
            aria-label="Close cart"
          >
            <IoCloseOutline />
          </button>
        </div>

        <div className="fashion-cart-drawer__scrollable">
          <Spin spinning={loading}>
            {itemCount > 0 ? (
              <div className="fashion-cart-drawer__items">
                {lineItems.map((cartItem, index) => {
                  const sizeLabel = getSizeLabel(cartItem);
                  const unitPrice = parseFloat(
                    String(cartItem?.price || "0").replace(/,/g, "")
                  );

                  return (
                    <div
                      key={cartItem.id || index}
                      className="fashion-cart-drawer__item"
                    >
                      <div className="fashion-cart-drawer__item-divider" />
                      <div className="fashion-cart-drawer__item-content">
                        <div className="fashion-cart-drawer__item-image">
                          <Image
                            src={cartItem?.img_url || defaultImage}
                            alt={cartItem?.title || "Product"}
                            width={90}
                            height={110}
                            style={{ objectFit: "cover" }}
                          />
                        </div>
                        <div className="fashion-cart-drawer__item-details">
                          <div className="fashion-cart-drawer__item-top-row">
                            <h4 className="fashion-cart-drawer__item-name">
                              {cartItem?.sku || cartItem?.title || "N/A"}
                            </h4>
                            <button
                              className="fashion-cart-drawer__remove-btn"
                              onClick={() => removeItem(cartItem)}
                              disabled={updatingItemId === cartItem.id}
                              aria-label="Remove item"
                            >
                              <RxCross2 />
                            </button>
                          </div>
                          {sizeLabel && (
                            <p className="fashion-cart-drawer__item-size">
                              Size: {sizeLabel}
                            </p>
                          )}
                          <p className="fashion-cart-drawer__item-price">
                            {defaultCurrency} {formatPrice(unitPrice)}
                          </p>

                          <div className="fashion-cart-drawer__quantity-controls">
                            <Spin
                              spinning={updatingItemId === cartItem.id}
                              size="small"
                            >
                              <div className="fashion-cart-drawer__quantity-box">
                                <button
                                  className="fashion-cart-drawer__qty-btn"
                                  onClick={() =>
                                    updateQuantity(
                                      cartItem,
                                      cartItem.quantity - 1
                                    )
                                  }
                                  disabled={
                                    cartItem.quantity <= 1 ||
                                    updatingItemId === cartItem.id
                                  }
                                >
                                  -
                                </button>
                                <span className="fashion-cart-drawer__qty-value">
                                  {cartItem.quantity}
                                </span>
                                <button
                                  className="fashion-cart-drawer__qty-btn"
                                  onClick={() =>
                                    updateQuantity(
                                      cartItem,
                                      cartItem.quantity + 1
                                    )
                                  }
                                  disabled={updatingItemId === cartItem.id}
                                >
                                  +
                                </button>
                              </div>
                            </Spin>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="fashion-cart-drawer__empty">
                <BsHandbag className="fashion-cart-drawer__empty-icon" />
                <p className="fashion-cart-drawer__empty-title">
                  Your cart is empty
                </p>
                <p className="fashion-cart-drawer__empty-subtitle">
                  Add items to get started
                </p>
              </div>
            )}
          </Spin>

          {itemCount > 0 && (
            <div className="fashion-cart-drawer__footer">
              <div className="fashion-cart-drawer__footer-divider" />
              <div className="fashion-cart-drawer__subtotal-row">
                <span className="fashion-cart-drawer__subtotal-label">
                  SUB-TOTAL:
                </span>
                <span className="fashion-cart-drawer__subtotal-value">
                  {defaultCurrency.toUpperCase()} {formatPrice(subtotal)}
                </span>
              </div>
              <p className="fashion-cart-drawer__shipping-note">
                Shipping is added at checkout
              </p>
              <button
                className="fashion-cart-drawer__go-to-cart-btn"
                onClick={handleGoToCart}
              >
                GO TO CART
              </button>
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
}
